import { BehaviorSubject, Subject, Subscription } from "rxjs";
import bearing from "@turf/bearing";
import distance from "@turf/distance";
import transformTranslate from "@turf/transform-translate";
import { point, featureCollection } from "@turf/helpers";
import { Feature, Point, FeatureCollection } from "geojson";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { CompositionMapObject, MoveEvent, Listener, ClickEvent, TypedFeature } from "../types/internal";
import { DrawingModifier } from "../types/store-schema";
import { DrawingModifierCreators, ModifyableDrawingTool } from "./drawing-modifiers";

/**
 * Manages object dragging behaviour.
 * Reponsible for calculation of new geometries based on translations
 * Responsible for cleaning up after mouse event listeners
 */
export class ObjectDrag extends ModifyableDrawingTool<TypedFeature> {
    private translation: BehaviorSubject<TypedFeature[]>;

    private translationOnRelease: Subject<TypedFeature[]>;

    private mouseMoveListener: Listener;

    private mouseUpListener: Listener;

    private objectPlaceListeners: Listener[];

    private mouseMoved = false;

    private centerPoint: Feature<Point>;

    private translationSubscriptions: Subscription[] = [];

    private featuresAsCollection: FeatureCollection<TypedFeature["geometry"], TypedFeature["properties"]>;

    public get latestTranslation() {
        return this.convertFeaturesToCompositionMapObjects(this.translation.value);
    }

    constructor(
        private features: CompositionMapObject[],
        centerLngLat: { lng: number; lat: number },
        private operations: {
            onMouseMove: (callback: (e: MoveEvent) => void) => Listener;
            onMouseUp: (callback: (e: MoveEvent) => void) => Listener;
            /** Any event listeners that should result in the moving object to be dropped/placed on the map (i.e. click/paste) */
            placementListeners: ((callback: (e: ClickEvent) => void) => Listener)[];
            onRightClick?: (callback: (e: { lng: number; lat: number }) => void) => Listener;
            isInvalidPlacement: (compositionObjects: CompositionMapObject[]) => boolean;
            userDraggedToInvalidPosition: () => void;
        },
        startingCursorLngLat: { lng: number; lat: number },
        initialPositionCallback: (
            features: {
                geojson: Feature<AnySupportedGeometry, MapObjectProperties>;
                layerId: string;
                objectId: string;
            }[]
        ) => void,
        drawingModifier: DrawingModifier,
        createDrawingModifier: DrawingModifierCreators<TypedFeature>
    ) {
        super(drawingModifier, createDrawingModifier);

        this.featuresAsCollection = featureCollection(this.features.map(({ geojson }) => geojson));
        this.centerPoint = point([centerLngLat.lng, centerLngLat.lat]);
        this.mouseMoveListener = operations.onMouseMove(this.onMouseMove.bind(this));
        this.objectPlaceListeners = this.operations.placementListeners.map((listener) => listener(this.onPlace.bind(this)));
        const initialTranslation = this.calculateTranslation(startingCursorLngLat);
        this.translation = new BehaviorSubject(initialTranslation);
        this.translationOnRelease = new Subject();

        // Before we move our cursor, we want to paint the object in it's starting cursor position
        const convertedTranslation = this.convertFeaturesToCompositionMapObjects(initialTranslation);
        initialPositionCallback(convertedTranslation);
    }

    private initialiseMouseUpListener() {
        this.mouseUpListener = this.operations.onMouseUp(this.onMouseUp.bind(this));
    }

    private onPlace({ lng, lat }: ClickEvent) {
        // Check if translation is valid
        const translatedFeatures = this.finalTranslation({ lng, lat });
        const convertedTranslation = this.convertFeaturesToCompositionMapObjects(translatedFeatures);
        const isInvalidTranslation = this.operations.isInvalidPlacement?.(convertedTranslation);
        if (isInvalidTranslation) {
            // Only show toast on place as that is onClick and keyboard shortcut
            this.operations.userDraggedToInvalidPosition();
        } else {
            this.translationOnRelease.next(translatedFeatures);
            this.objectPlaceListeners.forEach((listener) => listener.remove());
            this.mouseUpListener?.remove();
        }
    }

    private onMouseUp({ lng, lat }: { lng: number; lat: number }) {
        // Check if translation is valid
        const translatedFeatures = this.finalTranslation({ lng, lat });
        const convertedTranslation = this.convertFeaturesToCompositionMapObjects(translatedFeatures);
        const isInvalidTranslation = this.operations.isInvalidPlacement?.(convertedTranslation);
        if (!isInvalidTranslation) {
            this.translationOnRelease.next(translatedFeatures);
            this.mouseUpListener.remove();
        }
    }

    private onMouseMove({ lng, lat }: { lng: number; lat: number }) {
        if (!this.mouseMoved) {
            this.mouseMoved = true;
            this.initialiseMouseUpListener();
        }

        const translation = this.tryModify(this.calculateTranslation({ lng, lat }));

        this.translation.next(translation);
    }

    private calculateTranslation(position: { lng: number; lat: number }) {
        if (position == null) {
            return [];
        }
        const { lng, lat } = position;
        const newPoint = point([lng, lat]);
        const distanceMoved = distance(this.centerPoint, newPoint);
        const bearingMoved = bearing(this.centerPoint, newPoint);
        const newFeatureCollection = transformTranslate(this.featuresAsCollection, distanceMoved, bearingMoved);
        return newFeatureCollection.features;
    }

    private finalTranslation(coords: { lng: number; lat: number }) {
        const latestTranslation = this.getLatestTranslation();
        if (latestTranslation != null) {
            return latestTranslation;
        }
        return this.calculateTranslation(coords);
    }

    private convertFeaturesToCompositionMapObjects(features: Feature<AnySupportedGeometry, MapObjectProperties>[]) {
        return features.map((feature, index) => ({ geojson: feature, layerId: this.features[index].layerId, objectId: this.features[index].objectId, waypoints: undefined }));
    }

    /* Stop listening for mouse up events, useful if the user starts dragging the map */
    public abortMouseUpPlacement() {
        // If a mouse move has not happened yet, mouseUpListener will be null
        this.mouseUpListener?.remove();
    }

    /* Resume listening to mouse up events, useful for when the user is finished dragging */
    public resumeMouseUpPlacement() {
        this.initialiseMouseUpListener();
    }

    public onTranslate(subscription: (geometryChange: CompositionMapObject[]) => void) {
        this.translationSubscriptions.push(this.translation.subscribe((translation) => subscription(this.convertFeaturesToCompositionMapObjects(translation))));
        return this;
    }

    public onRelease(subscription: (geometryChange: CompositionMapObject[]) => void) {
        this.translationSubscriptions.push(this.translationOnRelease.subscribe((translation) => subscription(this.convertFeaturesToCompositionMapObjects(translation))));
        return this;
    }

    public updateLevel(level: number) {
        this.features = this.features.map((feature) => ({ ...feature, level, geojson: { ...feature.geojson, properties: { ...feature.geojson.properties, level } } }));
        this.featuresAsCollection = featureCollection(this.features.map(({ geojson }) => geojson));
        this.translation.next(this.featuresAsCollection.features);
    }

    public destroy() {
        super.destroy();
        this.mouseMoveListener.remove();
        this.mouseUpListener?.remove();
        this.objectPlaceListeners?.forEach((listener) => listener.remove());
        this.translationSubscriptions.forEach((subscription) => subscription.unsubscribe());
    }
}
